import {
	ChangeDetectionStrategy,
	Component,
	DestroyRef,
	effect,
	inject,
	InjectionToken,
	input,
	OnInit,
	output
} from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { FormControl } from "@angular/forms";
import { LaunchDarklyService, LD_FLAG } from "@core-services/launch-darkly/launch-darkly.service";
import { TradableListService } from "@main-services/currency/tradable_list.service";
import { Tradable } from "@models/interfaces/tradable";
import { VSNOptinToggleViewModel } from "@modules/web3/interface/web3.benefit.ui.interface";
import { distinctUntilChanged, map, Observable } from "rxjs";

export const VISION_BENEFIT_TOGGLE = new InjectionToken<VisionOptinToggleComponent>("vision-benefit-toggle");

@Component({
	selector: "web3-vision-optin-toggle",
	templateUrl: "./vision-optin-toggle.component.html",
	styleUrl: "./vision-optin-toggle.component.scss",
	changeDetection: ChangeDetectionStrategy.OnPush,
	providers: [
		{
			provide: VISION_BENEFIT_TOGGLE,
			useExisting: VisionOptinToggleComponent
		}
	],
	standalone: false
})
export class VisionOptinToggleComponent implements OnInit {
	private launchDarklyService = inject(LaunchDarklyService);
	private destroyRef = inject(DestroyRef);
	private tradableListService = inject(TradableListService);

	vm = input<VSNOptinToggleViewModel>();
	showToggle = input<boolean>(true);
	isLimitOrder = input<boolean>(false);
	onToggle = output<boolean>();
	vision$ = this.getVisionTradable$();
	toggleCtrl = new FormControl({ value: false, disabled: false });

	isDisabled = false;
	isError = false;
	private skipFirstCycle = false;

	constructor() {
		//listen to vm value coming from parent component
		effect(() => {
			const enabled = this.vm()?.vsnFeeCollection?.enabled;

			//skip first cycle to avoid setting toggle 2x
			if (this.skipFirstCycle) {
				this.toggleCtrl.setValue(enabled);
			}
			this.skipFirstCycle = true;
		});
	}

	public getVisionPid(): Observable<string> {
		return this.launchDarklyService
			.getFeatureConfig$<{
				pid: string;
			}>(LD_FLAG.VISION_PID)
			.pipe(map((data) => data.pid));
	}

	private getVisionTradable$(): Observable<Tradable> {
		return this.getVisionPid().pipe(
			map((pid) => {
				return this.tradableListService.getItemByPid(pid);
			})
		);
	}

	ngOnInit(): void {
		//set inital value of toggle
		const enabled = this.vm()?.vsnFeeCollection?.enabled;
		this.toggleCtrl.setValue(enabled);

		//send toggle value to parent component
		this.toggleCtrl.valueChanges
			.pipe(takeUntilDestroyed(this.destroyRef), distinctUntilChanged())
			.subscribe((value) => {
				this.onToggle.emit(value);
			});
	}
}
